import type { ContractPermissionsResponse } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodContractPermissionsResponseSchema } from './validations';

/**
 * Fetches field-level edit permissions for a contract based on its current status.
 * Use this to dynamically enable/disable form fields in the UI.
 *
 * @param contractId - The UUID of the contract to fetch permissions for.
 * @param options - Optional request options for the ky client.
 * @returns The editable and readonly field lists with current status.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_permissions_contracts__contract_id__permissions_get
 */
export const fetchContractPermissions = async (
  contractId: UUID,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.CONTRACT.PERMISSIONS(contractId), {
      ...options,
    })
    .json<ContractPermissionsResponse>();

  // Validate the response with Zod
  const validatedResponse =
    zodContractPermissionsResponseSchema.safeParse(response);
  if (validatedResponse?.error?.issues?.length) {
    log.error({
      error: {
        errorMessage: validatedResponse?.error?.message,
        errors: validatedResponse?.error?.issues,
        response,
      },
      message: 'Contract permissions API response has validation errors',
    });
  }

  return response;
};
